import {
  Group, Mesh,
  CylinderGeometry,
  MeshLambertMaterial, MeshPhongMaterial,
} from 'three';
import { quad, buildGeo } from './ModelUtils';

/**
 * ASF-2 オーシャンヴァイパー — 洋上支援戦闘機モデル
 *
 * F-2風: 6角形断面 × 10ステーション押し出し方式。
 * 特徴: 単発エンジン、大型主翼、腹部インテーク、単垂直尾翼、濃紺洋上迷彩。
 */
export interface MarineColorScheme {
  body: number;
  body2: number;
  dark: number;
  under: number;
  nozzleEmissive?: number;
}

export const MARINE_DEFAULT_COLORS: MarineColorScheme = {
  body:  0x102e5c,   // 濃紺（ダークネイビー）
  body2: 0x2a5a8f,   // 中間青（ミディアムブルー）
  dark:  0x0c2244,   // 影面
  under: 0x4878a8,   // 下面（明るめの青灰）
};

export const MARINE_ENEMY_COLORS: MarineColorScheme = {
  body: 0x8a2222, body2: 0xaa3333, dark: 0x551818, under: 0x6a1a1a, nozzleEmissive: 0xff2200,
};

export function buildMarineModel(colors: MarineColorScheme = MARINE_DEFAULT_COLORS): Group {
  const root = new Group();

  const matBody   = new MeshPhongMaterial({ color: colors.body, shininess: 30, flatShading: true });
  const matBody2  = new MeshPhongMaterial({ color: colors.body2, shininess: 30, flatShading: true });
  const matDark   = new MeshPhongMaterial({ color: colors.dark, shininess: 20, flatShading: true });
  const matUnder  = new MeshPhongMaterial({ color: colors.under, shininess: 15, flatShading: true });
  const matCanopy = new MeshPhongMaterial({
    color: 0x182838, emissive: 0x000c18, shininess: 250,
    transparent: true, opacity: 0.78,
  });
  const matNozzle = new MeshPhongMaterial({
    color: 0x252528, emissive: colors.nozzleEmissive ?? 0x100600, emissiveIntensity: 0.3, shininess: 100,
  });
  const matIntake = new MeshLambertMaterial({ color: 0x0e0e12 });
  const matRadome = new MeshPhongMaterial({ color: 0xd8d8d0, shininess: 40, flatShading: true });

  // ════════════════════════════════════════════════
  // 胴体（6角形断面 × 10ステーション）
  // F-2/F-16系の丸みのある細身胴体
  // ════════════════════════════════════════════════
  // 6角形断面: [T, TR, BR, B, BL, TL]
  //        T  (top)
  //       / \
  //     TL   TR (shoulder/cheek)
  //     |     |
  //     BL   BR (belly)
  //       \ /
  //        B  (bottom)
  const stations: { z: number; tw: number; ty: number; cw: number; cy: number; bw: number; by: number; boy: number }[] = [
    // レドーム先端
    { z: -11.0, tw: 0.00, ty: 0.05, cw: 0.05, cy: 0.02, bw: 0.03, by: -0.02, boy: -0.04 },
    // レドーム→コクピット前
    { z:  -9.0, tw: 0.18, ty: 0.30, cw: 0.42, cy: 0.10, bw: 0.35, by: -0.12, boy: -0.18 },
    // コクピット部
    { z:  -6.5, tw: 0.30, ty: 0.48, cw: 0.62, cy: 0.18, bw: 0.55, by: -0.18, boy: -0.28 },
    // インテーク前
    { z:  -4.5, tw: 0.35, ty: 0.52, cw: 0.72, cy: 0.15, bw: 0.65, by: -0.22, boy: -0.35 },
    // インテーク部（最大幅）
    { z:  -2.0, tw: 0.38, ty: 0.50, cw: 0.82, cy: 0.10, bw: 0.75, by: -0.25, boy: -0.42 },
    // 胴体中央（翼付根）
    { z:   0.0, tw: 0.36, ty: 0.48, cw: 0.80, cy: 0.08, bw: 0.72, by: -0.22, boy: -0.38 },
    // 胴体後半
    { z:   2.5, tw: 0.32, ty: 0.45, cw: 0.72, cy: 0.06, bw: 0.65, by: -0.20, boy: -0.35 },
    // エンジン部
    { z:   5.0, tw: 0.28, ty: 0.42, cw: 0.62, cy: 0.05, bw: 0.58, by: -0.18, boy: -0.32 },
    // ノズル前
    { z:   7.0, tw: 0.22, ty: 0.38, cw: 0.52, cy: 0.04, bw: 0.48, by: -0.14, boy: -0.28 },
    // ノズル後端
    { z:   8.5, tw: 0.18, ty: 0.32, cw: 0.45, cy: 0.02, bw: 0.42, by: -0.10, boy: -0.22 },
  ];

  function secVerts(s: typeof stations[0]): number[][] {
    return [
      [0,      s.ty,  s.z],  // 0: T  (top)
      [ s.cw,  s.cy,  s.z],  // 1: TR (cheek right)
      [ s.bw,  s.by,  s.z],  // 2: BR (belly right)
      [0,      s.boy, s.z],  // 3: B  (bottom)
      [-s.bw,  s.by,  s.z],  // 4: BL (belly left)
      [-s.cw,  s.cy,  s.z],  // 5: TL (cheek left)
    ];
  }

  // 胴体上面 (T→TR + TL→T) — 中間青のみ
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < stations.length - 1; i++) {
      const a = secVerts(stations[i]);
      const b = secVerts(stations[i + 1]);
      quad(v, n, a[0][0],a[0][1],a[0][2], b[0][0],b[0][1],b[0][2], b[1][0],b[1][1],b[1][2], a[1][0],a[1][1],a[1][2]);
      quad(v, n, a[5][0],a[5][1],a[5][2], b[5][0],b[5][1],b[5][2], b[0][0],b[0][1],b[0][2], a[0][0],a[0][1],a[0][2]);
    }
    root.add(new Mesh(buildGeo(v, n), matBody2));
  }

  // 胴体側面 (TR→BR + BL→TL)
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < stations.length - 1; i++) {
      const a = secVerts(stations[i]);
      const b = secVerts(stations[i + 1]);
      quad(v, n, a[1][0],a[1][1],a[1][2], b[1][0],b[1][1],b[1][2], b[2][0],b[2][1],b[2][2], a[2][0],a[2][1],a[2][2]);
      quad(v, n, a[4][0],a[4][1],a[4][2], b[4][0],b[4][1],b[4][2], b[5][0],b[5][1],b[5][2], a[5][0],a[5][1],a[5][2]);
    }
    root.add(new Mesh(buildGeo(v, n), matDark));
  }

  // 胴体下面 (BR→B→BL)
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < stations.length - 1; i++) {
      const a = secVerts(stations[i]);
      const b = secVerts(stations[i + 1]);
      quad(v, n, a[2][0],a[2][1],a[2][2], b[2][0],b[2][1],b[2][2], b[3][0],b[3][1],b[3][2], a[3][0],a[3][1],a[3][2]);
      quad(v, n, a[3][0],a[3][1],a[3][2], b[3][0],b[3][1],b[3][2], b[4][0],b[4][1],b[4][2], a[4][0],a[4][1],a[4][2]);
    }
    root.add(new Mesh(buildGeo(v, n), matUnder));
  }

  // レドーム（先端2ステーション分を白色で上書き）
  {
    const v: number[] = [], n: number[] = [];
    for (let i = 0; i < 2; i++) {
      const a = secVerts(stations[i]);
      const b = secVerts(stations[i + 1]);
      for (let j = 0; j < 6; j++) {
        const j2 = (j + 1) % 6;
        quad(v, n,
          a[j][0], a[j][1], a[j][2],
          b[j][0], b[j][1], b[j][2],
          b[j2][0], b[j2][1], b[j2][2],
          a[j2][0], a[j2][1], a[j2][2],
        );
      }
    }
    root.add(new Mesh(buildGeo(v, n), matRadome));
  }

  // ════════════════════════════════════════════════
  // キャノピー（バブルキャノピー、F-16/F-2風）
  // ════════════════════════════════════════════════
  {
    const v: number[] = [], n: number[] = [];
    const cp = [
      { z: -7.2, w: 0.00, h: 0.00 },
      { z: -6.2, w: 0.38, h: 0.35 },
      { z: -5.2, w: 0.42, h: 0.42 },
      { z: -4.2, w: 0.38, h: 0.30 },
      { z: -3.5, w: 0.20, h: 0.10 },
    ];
    for (let i = 0; i < cp.length - 1; i++) {
      const a = cp[i], b = cp[i + 1];
      const aBase = 0.46, bBase = 0.46;
      const aTop = aBase + a.h, bTop = bBase + b.h;
      // 上面
      quad(v, n, -a.w, aTop, a.z, -b.w, bTop, b.z, b.w, bTop, b.z, a.w, aTop, a.z);
      // 左面
      quad(v, n, -a.w, aBase, a.z, -b.w, bBase, b.z, -b.w, bTop, b.z, -a.w, aTop, a.z);
      // 右面
      quad(v, n, a.w, aTop, a.z, b.w, bTop, b.z, b.w, bBase, b.z, a.w, aBase, a.z);
    }
    root.add(new Mesh(buildGeo(v, n), matCanopy));
  }

  // ════════════════════════════════════════════════
  // 主翼（大型クリップドデルタ翼 — F-2の最大の特徴）
  // F-16より約25%広い翼面積
  // ════════════════════════════════════════════════
  for (const sx of [-1, 1]) {
    const rootLE_x = sx * 0.75, rootLE_z = -2.5;
    const rootTE_x = sx * 0.75, rootTE_z =  4.0;
    const midLE_x  = sx * 3.8,  midLE_z  = -0.5;
    const midTE_x  = sx * 3.1,  midTE_z  =  3.9;
    const tipLE_x  = sx * 6.8,  tipLE_z  =  1.5;
    const tipTE_x  = sx * 5.5,  tipTE_z  =  3.8;
    const rootY = -0.08, midY = -0.12, tipY = -0.15;
    const thick = 0.10, midThick = 0.06, tipThick = 0.03;

    // 内側翼（濃紺）
    {
      const v: number[] = [], n: number[] = [];
      // 上面
      quad(v, n,
        rootLE_x, rootY+thick, rootLE_z,
        midLE_x,  midY+midThick, midLE_z,
        midTE_x,  midY+midThick, midTE_z,
        rootTE_x, rootY+thick, rootTE_z,
      );
      // 下面
      quad(v, n,
        rootLE_x, rootY-thick, rootLE_z,
        rootTE_x, rootY-thick, rootTE_z,
        midTE_x,  midY-midThick, midTE_z,
        midLE_x,  midY-midThick, midLE_z,
      );
      // 前縁
      quad(v, n,
        rootLE_x, rootY-thick, rootLE_z,
        midLE_x,  midY-midThick, midLE_z,
        midLE_x,  midY+midThick, midLE_z,
        rootLE_x, rootY+thick, rootLE_z,
      );
      // 後縁
      quad(v, n,
        rootTE_x, rootY+thick, rootTE_z,
        midTE_x,  midY+midThick, midTE_z,
        midTE_x,  midY-midThick, midTE_z,
        rootTE_x, rootY-thick, rootTE_z,
      );
      root.add(new Mesh(buildGeo(v, n), matBody));
    }

    // 外側翼（中間青）
    {
      const v: number[] = [], n: number[] = [];
      // 上面
      quad(v, n,
        midLE_x, midY+midThick, midLE_z,
        tipLE_x, tipY+tipThick, tipLE_z,
        tipTE_x, tipY+tipThick, tipTE_z,
        midTE_x, midY+midThick, midTE_z,
      );
      // 下面
      quad(v, n,
        midLE_x, midY-midThick, midLE_z,
        midTE_x, midY-midThick, midTE_z,
        tipTE_x, tipY-tipThick, tipTE_z,
        tipLE_x, tipY-tipThick, tipLE_z,
      );
      // 前縁
      quad(v, n,
        midLE_x, midY-midThick, midLE_z,
        tipLE_x, tipY-tipThick, tipLE_z,
        tipLE_x, tipY+tipThick, tipLE_z,
        midLE_x, midY+midThick, midLE_z,
      );
      // 後縁
      quad(v, n,
        midTE_x, midY+midThick, midTE_z,
        tipTE_x, tipY+tipThick, tipTE_z,
        tipTE_x, tipY-tipThick, tipTE_z,
        midTE_x, midY-midThick, midTE_z,
      );
      // 翼端
      quad(v, n,
        tipLE_x, tipY+tipThick, tipLE_z,
        tipLE_x, tipY-tipThick, tipLE_z,
        tipTE_x, tipY-tipThick, tipTE_z,
        tipTE_x, tipY+tipThick, tipTE_z,
      );
      root.add(new Mesh(buildGeo(v, n), matBody2));
    }

    // パイロン + ミサイル
    const py = new Mesh(new CylinderGeometry(0.04, 0.04, 0.25, 4), matDark);
    py.position.set(sx * 3.8, rootY - 0.22, 1.8);
    root.add(py);
    const ml = new Mesh(new CylinderGeometry(0.07, 0.07, 1.6, 6), matDark);
    ml.rotation.x = Math.PI / 2;
    ml.position.set(sx * 3.8, rootY - 0.40, 1.8);
    root.add(ml);
  }

  // ════════════════════════════════════════════════
  // 水平尾翼
  // ════════════════════════════════════════════════
  for (const sx of [-1, 1]) {
    const v: number[] = [], n: number[] = [];
    const rle_x = sx * 0.6, rle_z = 5.5;
    const rte_x = sx * 0.7, rte_z = 7.8;
    const tle_x = sx * 3.2, tle_z = 7.0;
    const tte_x = sx * 2.5, tte_z = 8.2;
    const y = 0.0, t = 0.05;

    quad(v, n, rle_x, y+t, rle_z, tle_x, y+t, tle_z, tte_x, y+t, tte_z, rte_x, y+t, rte_z);
    quad(v, n, rle_x, y-t, rle_z, rte_x, y-t, rte_z, tte_x, y-t, tte_z, tle_x, y-t, tle_z);
    quad(v, n, rle_x, y-t, rle_z, tle_x, y-t, tle_z, tle_x, y+t, tle_z, rle_x, y+t, rle_z);
    quad(v, n, rte_x, y+t, rte_z, tte_x, y+t, tte_z, tte_x, y-t, tte_z, rte_x, y-t, rte_z);

    root.add(new Mesh(buildGeo(v, n), matDark));
  }

  // ════════════════════════════════════════════════
  // 垂直尾翼（単垂直尾翼 — F-2/F-16の特徴）
  // ════════════════════════════════════════════════
  {
    const rb_x = 0, rb_z = 4.5, rb_y = 0.42;
    const rt_x = 0, rt_z = 7.5, rt_y = 0.42;
    const tb_x = 0, tb_z = 5.5, tb_y = 3.0;
    const tt_x = 0, tt_z = 7.2, tt_y = 2.2;
    const t = 0.08;

    // 右面（中間青）
    const vR: number[] = [], nR: number[] = [];
    quad(vR, nR, rb_x+t, rb_y, rb_z, rt_x+t, rt_y, rt_z, tt_x+t, tt_y, tt_z, tb_x+t, tb_y, tb_z);
    root.add(new Mesh(buildGeo(vR, nR), matBody2));
    // 左面（濃紺）
    const vL: number[] = [], nL: number[] = [];
    quad(vL, nL, rt_x-t, rt_y, rt_z, rb_x-t, rb_y, rb_z, tb_x-t, tb_y, tb_z, tt_x-t, tt_y, tt_z);
    root.add(new Mesh(buildGeo(vL, nL), matBody));
    // 前縁・後縁・先端
    const vE: number[] = [], nE: number[] = [];
    quad(vE, nE, rb_x-t, rb_y, rb_z, rb_x+t, rb_y, rb_z, tb_x+t, tb_y, tb_z, tb_x-t, tb_y, tb_z);
    quad(vE, nE, rt_x+t, rt_y, rt_z, rt_x-t, rt_y, rt_z, tt_x-t, tt_y, tt_z, tt_x+t, tt_y, tt_z);
    quad(vE, nE, tb_x-t, tb_y, tb_z, tb_x+t, tb_y, tb_z, tt_x+t, tt_y, tt_z, tt_x-t, tt_y, tt_z);
    root.add(new Mesh(buildGeo(vE, nE), matDark));
  }

  // ════════════════════════════════════════════════
  // 腹部インテーク（F-16/F-2風のチンインテーク）
  // ════════════════════════════════════════════════
  {
    const v: number[] = [], n: number[] = [];
    // インテーク開口部（胴体下面に統合）
    const ix = 0.30, iz_f = -3.5, iz_r = -1.5;
    const iy_top = -0.30, iy_bot = -0.55;

    // 左壁
    quad(v, n, -ix, iy_top, iz_f, -ix, iy_top, iz_r, -ix, iy_bot, iz_r, -ix, iy_bot, iz_f);
    // 右壁
    quad(v, n,  ix, iy_top, iz_r,  ix, iy_top, iz_f,  ix, iy_bot, iz_f,  ix, iy_bot, iz_r);
    // 底面
    quad(v, n, -ix, iy_bot, iz_f,  -ix, iy_bot, iz_r,  ix, iy_bot, iz_r,  ix, iy_bot, iz_f);
    // 奥面（暗い）
    quad(v, n, -ix, iy_top, iz_r,  ix, iy_top, iz_r,  ix, iy_bot, iz_r, -ix, iy_bot, iz_r);

    root.add(new Mesh(buildGeo(v, n), matIntake));
  }

  // ════════════════════════════════════════════════
  // エンジンノズル（単発）
  // ════════════════════════════════════════════════
  {
    const outer = new Mesh(new CylinderGeometry(0.40, 0.48, 1.8, 12), matDark);
    outer.rotation.x = Math.PI / 2;
    outer.position.set(0, -0.05, 7.8);
    root.add(outer);

    const inner = new Mesh(new CylinderGeometry(0.30, 0.30, 0.4, 12), matNozzle);
    inner.rotation.x = Math.PI / 2;
    inner.position.set(0, -0.05, 8.8);
    root.add(inner);
  }

  // ════════════════════════════════════════════════
  // ピトー管
  // ════════════════════════════════════════════════
  const pitot = new Mesh(new CylinderGeometry(0.025, 0.025, 1.5, 5), matDark);
  pitot.rotation.x = Math.PI / 2;
  pitot.position.set(0, 0.10, -11.5);
  root.add(pitot);

  return root;
}
